import React, { useState, useEffect } from "react";
import client from "../../lib/api";
import { mediaSrc } from "../../lib/media";
import { STAGES, fmtGbp } from "../../mock";
import { ScoreRing } from "../../components/widgets";
import { Checkbox } from "../../components/ui/checkbox";
import {
  TrendingUp, TrendingDown, Sparkles, ArrowUpRight, Zap, MessageSquare,
  Trophy, Bell, GitBranch, ChevronRight,
} from "lucide-react";
import { tr } from "../../i18n/LangContext";

function TrendChart({ data }) {
  const w = 520, h = 150, pad = 8;
  const max = Math.max(...data.map((d) => d.v)) * 1.1;
  const min = Math.min(...data.map((d) => d.v)) * 0.85;
  const x = (i) => pad + (i * (w - pad * 2)) / (data.length - 1);
  const y = (v) => h - pad - ((v - min) / (max - min)) * (h - pad * 2);
  const line = data.map((d, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(d.v)}`).join(" ");
  const area = `${line} L ${x(data.length - 1)} ${h} L ${x(0)} ${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-[150px]">
      <defs>
        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1aa860" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#1aa860" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#areaGrad)" />
      <path d={line} fill="none" stroke="#1aa860" strokeWidth="2.5" strokeLinecap="round" />
      {data.map((d, i) => (
        <g key={i}>
          <circle cx={x(i)} cy={y(d.v)} r="3" fill="#1aa860" />
          <text x={x(i)} y={h - 2} textAnchor="middle" className="fill-muted-foreground" fontSize="9">{d.m}</text>
        </g>
      ))}
    </svg>
  );
}

const actIcon = { score: Zap, deal: GitBranch, message: MessageSquare, win: Trophy, alert: Bell };

export default function Overview({ onNavigate }) {
  const [data, setData] = useState(null);
  const [properties, setProperties] = useState([]);
  const [managers, setManagers] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [d, p, m] = await Promise.all([
          client.get("/dashboard"), client.get("/properties"), client.get("/my/managers"),
        ]);
        setData(d.data);
        setProperties(p.data.filter((x) => x.disposition !== "disqualified"));
        setManagers(m.data.managers || []);
      } catch (e) {
        console.error("Dashboard load failed", e); // auth failures also redirect
      }
    };
    load();
  }, []);

  if (!data) {
    return (
      <div className="grid h-64 place-items-center">
        <div className="h-8 w-8 rounded-full border-2 border-brand-red border-t-transparent animate-spin" />
      </div>
    );
  }

  const { kpis: KPIS, trend: PIPELINE_TREND, activities: ACTIVITIES, tasks: TASKS, insights: AI_INSIGHTS } = data;
  const focus = [...properties].filter((p) => p.stage !== "acquired").sort((a, b) => b.score - a.score).slice(0, 3);
  return (
    <div className="space-y-6">
      {managers.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-white/[0.07] bg-card/40 px-5 py-3" data-testid="my-managers">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            Your success {managers.length === 1 ? "manager" : "managers"}
          </span>
          {managers.map((m) => (
            <span key={m.user_id} data-testid="my-manager-chip"
              className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs ${m.lead
                ? "border-brand-green/30 bg-brand-green/10 text-brand-green" : "border-white/12 bg-white/[0.03] text-slate-300"}`}>
              {m.name}{m.lead && <span className="text-[10px] uppercase tracking-wide opacity-80">{tr("lead")}</span>}
            </span>
          ))}
          {managers[0]?.email && (
            <a href={`mailto:${managers[0].email}`} className="text-xs text-brand-red hover:underline" data-testid="manager-email-link">
              {tr("Get in touch")}
            </a>
          )}
        </div>
      )}

      {/* KPI row */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {KPIS.map((k) => (
          <div key={k.id} className="glass rounded-2xl p-5 transition-transform hover:-translate-y-0.5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{tr(k.label)}</p>
            <div className="mt-2 flex items-end justify-between">
              <span className="font-display text-3xl font-semibold text-white">{k.value}</span>
              <span className={`flex items-center gap-1 text-xs font-medium ${k.up ? "text-brand-green" : "text-brand-red"}`}>
                {k.up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}{k.delta}
              </span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{/^across \d+ deals?$/.test(k.sub) ? `${tr("across")} ${k.sub.split(" ")[1]} ${tr(k.sub.endsWith("s") ? "deals" : "deal")}` : tr(k.sub)}</p>
          </div>
        ))}
      </div>

      {(PIPELINE_TREND.length > 0 || AI_INSIGHTS.length > 0) && (
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Pipeline value chart */}
        {PIPELINE_TREND.length > 0 && (
        <div className="glass rounded-2xl p-5 lg:col-span-2" data-testid="pipeline-trend-card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-base font-semibold text-white">{tr("Pipeline value")}</h3>
              <p className="text-xs text-muted-foreground">Trailing {PIPELINE_TREND.length} months (£M)</p>
            </div>
          </div>
          <div className="mt-4"><TrendChart data={PIPELINE_TREND} /></div>
        </div>
        )}

        {/* AI spotlight */}
        {AI_INSIGHTS.length > 0 && (
        <div className="glass rounded-2xl p-5" data-testid="ai-spotlight-card">
          <div className="mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-brand-red" />
            <h3 className="font-display text-base font-semibold text-white">{tr("AI spotlight")}</h3>
          </div>
          <div className="space-y-3">
            {AI_INSIGHTS.slice(0, 2).map((i) => (
              <div key={i.id} className={`rounded-xl border p-3 ${i.tone === "green" ? "border-brand-green/20 bg-brand-green/5" : "border-brand-red/20 bg-brand-red/5"}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-white">{i.title}</p>
                  <span className={`text-xs font-semibold ${i.tone === "green" ? "text-brand-green" : "text-brand-red"}`}>{i.confidence}%</span>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{i.body}</p>
              </div>
            ))}
          </div>
          <button onClick={() => onNavigate("insights")} className="mt-3 flex items-center gap-1 text-xs font-medium text-brand-red hover:text-brand-green transition-colors">
            View all insights <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
        )}
      </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Focus deals */}
        <div className={`glass rounded-2xl p-5 ${(ACTIVITIES.length > 0 || TASKS.length > 0) ? "lg:col-span-2" : "lg:col-span-3"}`}>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display text-base font-semibold text-white">{tr("Top focus deals")}</h3>
            <button onClick={() => onNavigate("properties")} className="text-xs text-brand-red hover:text-brand-green transition-colors">{tr("See all")}</button>
          </div>
          <div className="space-y-3">
            {focus.map((p) => {
              const stage = STAGES.find((s) => s.id === p.stage) || { color: "#7c8a99", label: p.stage || "Unknown" };
              return (
                <div key={p.id} className="flex items-center gap-4 rounded-xl border border-white/5 bg-white/[0.02] p-3 transition-colors hover:bg-white/[0.04]">
                  <img src={mediaSrc(p.image)} alt="" className="h-14 w-14 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-white">{p.title}</p>
                    <p className="text-xs text-muted-foreground">{p.district} · {p.beds} {tr("Beds").toLowerCase()} · {fmtGbp(p.askGbp)}</p>
                    <span className="mt-1 inline-flex items-center gap-1 text-[11px]" style={{ color: stage.color }}>
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: stage.color }} />{tr(stage.label)}
                    </span>
                  </div>
                  <ScoreRing score={p.score} size={46} />
                </div>
              );
            })}
          </div>
        </div>

        {/* Activity + tasks */}
        {(ACTIVITIES.length > 0 || TASKS.length > 0) && (
        <div className="space-y-6">
          {ACTIVITIES.length > 0 && (
          <div className="glass rounded-2xl p-5" data-testid="activity-card">
            <h3 className="mb-3 font-display text-base font-semibold text-white">{tr("Activity")}</h3>
            <div className="space-y-3">
              {ACTIVITIES.slice(0, 4).map((a) => {
                const Icon = actIcon[a.type] || Zap;
                return (
                  <div key={a.id} className="flex gap-3">
                    <div className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-white/5"><Icon className="h-3.5 w-3.5 text-brand-green" /></div>
                    <div><p className="text-xs leading-snug text-white/90">{a.text}</p><p className="text-[10px] text-muted-foreground">{a.time}</p></div>
                  </div>
                );
              })}
            </div>
          </div>
          )}
          {TASKS.length > 0 && (
          <div className="glass rounded-2xl p-5" data-testid="tasks-card">
            <h3 className="mb-3 font-display text-base font-semibold text-white">{tr("Today's tasks")}</h3>
            <div className="space-y-2.5">
              {TASKS.slice(0, 4).map((t) => (
                <label key={t.id} className="flex items-start gap-2.5 cursor-pointer">
                  <Checkbox defaultChecked={t.done} className="mt-0.5 data-[state=checked]:bg-brand-green data-[state=checked]:border-brand-green" />
                  <div className="flex-1">
                    <p className={`text-xs ${t.done ? "text-muted-foreground line-through" : "text-white/90"}`}>{t.text}</p>
                    <p className="text-[10px] text-muted-foreground">{t.due}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
